import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Skeleton from '../common/Skeleton';
import { Save, Check, CircleMinus, CircleCheck, Lock, Edit2 } from 'lucide-react';
import riskDriversService, { RiskDriver } from '../../services/riskDriversService';
import Modal from '../common/Modal';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import Pagination from '../common/Pagination';
import Toast, { ToastMessage } from '../common/Toast';
import { confirmWarning, confirmActive } from '../common/confirm';
import extractApiError from '../../utils/apiError';
import NoDataFound from '../common/NoDataFound';
import ApprovalDot from '../common/ApprovalDot';
import WorkflowModal from '../common/WorkflowModal';
import { usePermission } from '../../contexts/PermissionContext';
import { RBAC_ACTIVITY } from './rbacMock';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea } from '../../validation/ValidatedFields';
import { isUniqueLocal } from '../../validation/uniqueness';

interface RiskDriversTabProps {
  createTrigger?: number;
  pageSize: number;
  onPageSizeChange?: (size: number) => void;
  searchTerm: string;
  statusFilter: string;
}

type WorkflowRecord = { workflow_state: 'draft' | 'submitted' | 'approved' | 'rejected' };
type RowWithWorkflow = RiskDriver & WorkflowRecord;

const RiskDriversTab: React.FC<RiskDriversTabProps> = ({ createTrigger, pageSize, onPageSizeChange, searchTerm, statusFilter }) => {
  const viewPerm = usePermission(RBAC_ACTIVITY, 'view');
  const modifyPerm = usePermission(RBAC_ACTIVITY, 'view+modify');
  const canView = viewPerm.allowed;
  const canModify = modifyPerm.allowed;
  const permsLoading = viewPerm.loading;
  // Note: approve/reject/reopen gating is resolved by the WF engine inside
  // <WorkflowModal> (it returns per-action permissions), so no canApprove here.

  const [drivers, setDrivers] = useState<RowWithWorkflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [selectedDriver, setSelectedDriver] = useState<RowWithWorkflow | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  // View-first edit: existing records open read-only; "Edit" enables the form.
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<RiskDriver>>({});
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const fieldVh = useFieldValidation();

  const [workflowOpenFor, setWorkflowOpenFor] = useState<RowWithWorkflow | null>(null);

  const loadDrivers = useCallback(async () => {
    if (!canView) return;
    try {
      setLoading(true);
      const data = await riskDriversService.getAll({
        status: statusFilter as any,
        search: searchTerm || undefined,
        limit: 100
      });
      const sorted = [...data].sort((a, b) => (b.id || 0) - (a.id || 0));
      setDrivers(sorted as RowWithWorkflow[]);
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Failed to load risk drivers' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm, canView]);

  useEffect(() => { loadDrivers(); }, [loadDrivers]);
  useEffect(() => { setPage(1); }, [searchTerm, statusFilter, pageSize]);
  useEffect(() => { if (createTrigger && canModify) handleAddNew(); }, [createTrigger]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setSubmitting(false);
    setToggling(false);
  }, [showModal]);

  const handleRowClick = (driver: RowWithWorkflow) => {
    setSelectedDriver(driver);
    setFormData(driver);
    fieldVh.reset();
    setIsEditMode(true);
    setIsEditing(false); // open read-only; "Edit" enables the form
    setShowModal(true);
  };

  const handleAddNew = () => {
    if (!canModify) return;
    setSelectedDriver(null);
    setFormData({ driver_name: '', description: '', is_active: 1 });
    fieldVh.reset();
    setIsEditMode(false);
    setIsEditing(true); // create is always editable
    setShowModal(true);
  };

  const isApproved = selectedDriver?.workflow_state === 'approved';
  const fieldsDisabled = isEditMode && (!isEditing || !canModify || isApproved);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (fieldsDisabled) return;
    const ok = fieldVh.validateAll([
      { name: 'driver_name', code: 109, value: formData.driver_name || '', required: true },
      { name: 'description', code: 110, value: formData.description || '' },
    ]);
    if (!ok) return;
    if (!isUniqueLocal(909, (formData.driver_name || ''), drivers, selectedDriver?.id)) {
      fieldVh.setError('driver_name', 'Driver with this name already exists');
      return;
    }
    setSubmitting(true);
    try {
      if (isEditMode && selectedDriver) {
        const { is_active: _ia, ...updateData } = formData;
        await riskDriversService.update(selectedDriver.id, updateData);
        setToast({ type: 'success', message: 'Risk Driver updated successfully.' });
      } else {
        await riskDriversService.create(formData);
        setToast({ type: 'success', message: 'Risk Driver created successfully.' });
      }
      setShowModal(false);
      loadDrivers();
    } catch (err: any) {
      const msg = extractApiError(err, 'Operation failed');
      if (/exist|duplicate|unique/i.test(msg)) {
        fieldVh.setError('driver_name', msg);
      } else {
        setToast({ type: 'error', message: msg });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async () => {
    if (!selectedDriver || !canModify || isApproved) return;
    const next = selectedDriver.is_active ? 0 : 1;
    const confirmFn = selectedDriver.is_active ? confirmWarning : confirmActive;
    const ok = await confirmFn({
      title: next ? 'Reactivate this driver?' : 'Inactivate this driver?',
      text: next ? 'It will appear in active lists again.' : 'It will be hidden from active lists.',
      confirmText: next ? 'Yes, Reactivate' : 'Yes, Inactivate',
    });
    if (!ok) return;
    setToggling(true);
    try {
      await riskDriversService.toggleStatus(selectedDriver.id);
      setShowModal(false);
      setToast({ type: next ? 'success' : 'warning', message: next ? 'Driver Reactivated.' : 'Driver Inactivated.' });
      loadDrivers();
    } catch (err: any) {
      setToast({ type: 'error', message: extractApiError(err, 'Failed to update status') });
    } finally {
      setToggling(false);
    }
  };

  // Workflow (submit/approve/reject/reopen) is now driven end-to-end by the
  // unified <WorkflowModal> via the /api/v1/wf engine (kind="rsk_driver").

  const totalRecords = drivers.length;
  const paginatedRows = useMemo(
    () => drivers.slice((page - 1) * pageSize, page * pageSize),
    [drivers, page, pageSize],
  );

  if (!permsLoading && !canView) {
    return (
      <NoDataFound
        title="Not authorised"
        message={viewPerm.message || 'You are not authorised to view Risk Masters. Please contact your Super Admin.'}
      />
    );
  }

  return (
    <>
      <div className="scroll-container flex-1 overflow-hidden">
        <div className="table-list">
          <div className="table-list-scroll">
            <table className="table-list-table">
              <thead>
                <tr>
                  <th className="table-list-th table-list-cell--dot" aria-label="Workflow status"></th>
                  <th className="table-list-th table-list-cell--sno">S.No</th>
                  <th className="table-list-th table-list-cell--sno">Code</th>
                  <th className="table-list-th">Driver Name</th>
                  <th className="table-list-th">Description</th>
                </tr>
              </thead>
              <tbody aria-busy={loading} aria-live="polite">
                {loading && <Skeleton.TableRows cols={['3xs', '3xs', '3xs', 'md', '3xs']} />}
                {!loading && drivers.length === 0 && (
                  <tr>
                    <td colSpan={5} className="table-list-cell">
                      <NoDataFound
                        title="No Risk Drivers Found"
                        message={canModify ? 'Click "Create" to add one.' : 'No records to display.'}
                      />
                    </td>
                  </tr>
                )}
                {!loading && paginatedRows.map((driver, idx) => (
                    <tr
                      key={driver.id}
                      className={`table-list-row${driver.is_active === 1 ? '' : ' is-inactive'}`}
                      data-testid={`BTN_risk_driver_row_${driver.id}_open`}
                      onClick={() => handleRowClick(driver)}
                    >
                      <td
                        className="table-list-cell table-list-cell--dot"
                        onClick={(e) => { e.stopPropagation(); setWorkflowOpenFor(driver); }}
                        title="Workflow — view timeline & act"
                        data-testid={`BTN_risk_driver_row_${driver.id}_workflow`}
                      >
                        <ApprovalDot state={driver.workflow_state} onClick={() => setWorkflowOpenFor(driver)} />
                      </td>
                      <td className="table-list-cell table-list-cell--sno" data-testid={`BTN_risk_driver_row_${driver.id}_sno`}>
                        {(page - 1) * pageSize + idx + 1}
                      </td>
                      <td className="table-list-cell table-list-cell--sno" data-testid={`BTN_risk_driver_row_${driver.id}_code`}>
                        {driver.code
                          ? <span className="cell-code">{driver.code}</span>
                          : <span style={{ color: 'var(--clr-muted, #aaa)', fontSize: 12 }}>—</span>}
                      </td>
                      <td className="table-list-cell">
                        <span className="table-list-cell-title cell-name-lock">
                          {driver.workflow_state === 'approved' && (
                            <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                          )}
                          {driver.driver_name}
                        </span>
                      </td>
                      <td className="table-list-cell table-list-cell--truncate" title={driver.description || ''}>
                        {driver.description || <span className="text-gray-400 italic">—</span>}
                      </td>
                    </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            pageSize={pageSize}
            totalRecords={totalRecords}
            onPageChange={setPage}
            onPageSizeChange={onPageSizeChange}
            recordLabel="drivers"
          />
        </div>
      </div>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} size="md" data-testid="MOD_risk_driver_form">
        <Modal.Header>
          {isEditMode && selectedDriver
            ? <>{isApproved ? 'Viewing (locked)' : (canModify && isEditing) ? 'Editing' : 'Viewing'} "<span className="item-highlight">{selectedDriver.driver_name}</span>"</>
            : 'Create Risk Driver'}
        </Modal.Header>
        <Modal.Body>
          {isApproved && (
            <div className="approved-banner">
              <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
            </div>
          )}
          <form id="driver-form" onSubmit={handleSubmit} className="flex flex-col gap-y-4">
            {/* System code — read-only, prefilled with the same code the list shows. */}
            {isEditMode && (
              <FormControl.Input
                label="Code"
                value={(selectedDriver as any)?.code || '—'}
                disabled
                data-testid="INP_risk_driver_code"
              />
            )}
            <ValidatedInput
              vh={fieldVh}
              name="driver_name"
              code={109}
              required
              label="Driver Name"
              type="text"
              data-testid="INP_risk_driver_name"
              value={formData.driver_name || ''}
              onChange={e => setFormData({ ...formData, driver_name: e.target.value })}
              placeholder="e.g., Vendor Concentration, Employee Turnover Rate"
              disabled={fieldsDisabled}
            />
            <ValidatedTextarea
              vh={fieldVh}
              name="description"
              code={110}
              label="Description"
              data-testid="INP_risk_driver_description"
              value={formData.description || ''}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              placeholder="Describe this risk driver…"
              disabled={fieldsDisabled}
            />
          </form>
        </Modal.Body>
        <Modal.Footer className={isEditMode ? 'app-modal-footer--split' : ''}>
          {isEditMode && selectedDriver && canModify && !isApproved && (
            <Button
              variant={selectedDriver.is_active ? 'inactive-outline' : 'active-outline'}
              icon={selectedDriver.is_active ? CircleMinus : CircleCheck}
              data-testid="BTN_risk_driver_toggle_active"
              onClick={handleToggleActive}
              disabled={toggling || submitting}
            >
              {toggling ? '...' : selectedDriver.is_active ? 'Inactive' : 'Active'}
            </Button>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            {/* Create → Submit. */}
            {!isEditMode && (
              <Button
                type="submit"
                form="driver-form"
                variant="fill"
                icon={Save}
                data-testid="BTN_risk_driver_submit"
                disabled={submitting || toggling}
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </Button>
            )}
            {/* View mode → "Edit" enables the form (not for approved/locked). */}
            {isEditMode && canModify && !isApproved && !isEditing && (
              <Button
                variant="fill"
                icon={Edit2}
                data-testid="BTN_risk_driver_edit"
                onClick={() => setIsEditing(true)}
                disabled={submitting || toggling}
              >
                Edit
              </Button>
            )}
            {/* Edit mode → "Update" saves. */}
            {isEditMode && canModify && !isApproved && isEditing && (
              <Button
                type="submit"
                form="driver-form"
                variant="update"
                icon={Check}
                data-testid="BTN_risk_driver_update"
                disabled={submitting || toggling}
              >
                {submitting ? 'Updating...' : 'Update'}
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>

      <WorkflowModal
        isOpen={!!workflowOpenFor}
        onClose={() => setWorkflowOpenFor(null)}
        kind="rsk_driver"
        id={workflowOpenFor ? Number(workflowOpenFor.id) : null}
        onChanged={loadDrivers}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
};

export default RiskDriversTab;
